/**
 * Badminton Match Engine — wraps existing BWF rally-point pure functions.
 */

import {
  initScoringMatch,
  startNextGameFromCourtSetup,
  matchToLiveView,
  serializeScoringMatch as serializeRaw,
  deserializeScoringMatch as deserializeRaw,
  recordRally,
  undoLastRally,
  canUndoRally,
  resumeFromInterval,
  suggestCourtSetupAfterGame,
  syncTeamsToActiveGameBoxes,
  setsToWinToBestOf,
} from "../../../matchScoringBridge";
import { isGamePoint, isMatchPoint } from "../../../matchScoring";
import { hasActiveIntervalBreak } from "../../../matchInterval";
import { isAwaitingCourtSetup } from "../../../matchCourtSetup";
import {
  wrapSerializedMatch,
  unwrapSerializedMatch,
} from "../../contracts/MatchEngine";

export const BADMINTON_ENGINE_ID = "badminton.rally.v1";
export const BADMINTON_SCORING_SYSTEM = "rally";
const SCHEMA_VERSION = 1;

/**
 * @returns {import("../../contracts/MatchEngine").MatchEngine}
 */
export function createBadmintonMatchEngine() {
  const engine = {
    engineId: BADMINTON_ENGINE_ID,
    scoringSystem: BADMINTON_SCORING_SYSTEM,

    startMatch(config) {
      return initScoringMatch(
        config.teamA,
        config.teamB,
        config.setsToWin,
        !!config.endsSwapped,
        config.firstServerSide || "A",
        config.pointsToWin ?? 21
      );
    },

    startNextGame(match, config) {
      return startNextGameFromCourtSetup(
        match,
        config.teamA,
        config.teamB,
        !!config.endsSwapped,
        config.firstServerSide || "A",
        config.pointsToWin ?? 21
      );
    },

    scorePoint(match, side) {
      return recordRally(match, side);
    },

    fault(match) {
      // Badminton: no dedicated fault model — treat as no-op
      return match;
    },

    undo(match) {
      return undoLastRally(match);
    },

    canUndo(match) {
      return canUndoRally(match);
    },

    getState(match) {
      return match || null;
    },

    getLiveView(match) {
      return matchToLiveView(match);
    },

    getWinner(match) {
      return match?.matchWinner ?? null;
    },

    serialize(match) {
      if (!match) return null;
      const raw =
        typeof serializeRaw === "function"
          ? JSON.parse(serializeRaw(match) || "null")
          : match;
      return wrapSerializedMatch(BADMINTON_ENGINE_ID, SCHEMA_VERSION, raw);
    },

    deserialize(json) {
      const unwrapped = unwrapSerializedMatch(json);
      if (!unwrapped.state) {
        return deserializeRaw(json);
      }
      if (
        unwrapped.legacy ||
        !unwrapped.engineId ||
        unwrapped.engineId === BADMINTON_ENGINE_ID
      ) {
        return unwrapped.state;
      }
      return null;
    },

    isGamePoint(match) {
      return isGamePoint(match);
    },

    isMatchPoint(match) {
      return isMatchPoint(match);
    },

    hasInterval(match) {
      return hasActiveIntervalBreak(match);
    },

    resumeInterval(match, options = {}) {
      return resumeFromInterval(match, options);
    },

    isAwaitingCourtSetup(match) {
      return isAwaitingCourtSetup(match);
    },

    suggestCourtSetup(match) {
      return suggestCourtSetupAfterGame(match);
    },

    syncTeamsToBoxes(teamA, teamB, match) {
      return syncTeamsToActiveGameBoxes(teamA, teamB, match);
    },

    listFaultTypes() {
      return [];
    },

    setsToWinToBestOf,
  };

  return {
    ...engine,
    initScoringMatch(teamA, teamB, setsToWin, endsSwapped, firstServerSide, pointsToWin) {
      return engine.startMatch({
        teamA,
        teamB,
        setsToWin,
        endsSwapped,
        firstServerSide,
        pointsToWin,
      });
    },
    startNextGameFromCourtSetup(
      existing,
      teamA,
      teamB,
      endsSwapped,
      firstServerSide,
      pointsToWin
    ) {
      return engine.startNextGame(existing, {
        teamA,
        teamB,
        endsSwapped,
        firstServerSide,
        pointsToWin,
      });
    },
    matchToLiveView: engine.getLiveView,
    serializeScoringMatch: engine.serialize,
    deserializeScoringMatch: engine.deserialize,
    recordRally: engine.scorePoint,
    undoLastRally: engine.undo,
    canUndoRally: engine.canUndo,
    resumeFromInterval: engine.resumeInterval,
    suggestCourtSetupAfterGame: engine.suggestCourtSetup,
    syncTeamsToActiveGameBoxes: engine.syncTeamsToBoxes,
  };
}
